/**
 * Getting a new release to people without asking them to refresh, and without
 * ever throwing away what they were doing.
 *
 * How a release reaches a device:
 *  1. CHECK — the browser re-reads `/sw.js` (a conditional request answered
 *     `304` when nothing changed): at launch, when the app comes back to the
 *     foreground and at a bounded, jittered interval. Nothing else is fetched
 *     while there is no new release.
 *  2. DOWNLOAD — only when `/sw.js` differs: the new service worker fetches the
 *     files that changed, in the background, then WAITS. The running page keeps
 *     its own files: nothing breaks under the user's fingers.
 *  3. APPLY — at a moment that loses nothing: page change, return to the app,
 *     or a tab left in the background. While a form is being filled or the
 *     offline queue is not empty, nothing is applied automatically: a discreet
 *     prompt lets the user decide, and device copies of the forms are flushed
 *     before the reload.
 *
 * The decisions are pure functions (`decideUpdate`, `nextCheckDelay`…), tested
 * without a browser; the bottom of the file wires them to the service worker.
 */

/** No more than one check per device in this window, whatever triggers it. */
export const UPDATE_CHECK_MIN_GAP_MS = 15 * 60_000;
/** Periodic check while the app stays open, before jitter. */
export const UPDATE_CHECK_INTERVAL_MS = 60 * 60_000;
/** Each device shifts its periodic check by up to this share: no synchronized wave. */
export const UPDATE_CHECK_JITTER = 0.5;
/** A second automatic reload inside this window is refused: no reload loop. */
export const RELOAD_GUARD_MS = 60_000;
/** A release left waiting this long on an idle, visible page is offered instead of staying silent. */
export const IDLE_PROMPT_AFTER_MS = 5 * 60_000;
/** Answer expected by the installing worker from a page that drives its own update. */
export const UPDATE_AWARE_REPLY = 'myequip-update-aware';
export const UPDATE_PING = 'MYEQUIP_UPDATE_PING';
const RELOAD_AT_KEY = 'fa.update.reloadAt';
/** Wait after the app returns to the foreground before deciding anything. */
export const RESUME_SETTLE_MS = 2500;
/** Navigation chrome: using it is not work in progress on the page. */
const CHROME_SELECTOR = '.side-nav, .bottom-nav, .app-topbar, .portal-topbar, .admin-topbar, .app-update';
/** Second look after a page change, when a save was still in flight at the first. */
const NAVIGATION_RETRY_MS = 2000;
/** A page still here this long after the browser started leaving it has stayed after all. */
export const DEPARTURE_SETTLE_MS = 5000;

/**
 * - `current`: nothing to do;
 * - `ready`: a new release is downloaded and waiting for this page's go-ahead;
 * - `stale`: the new release already took over (another tab applied it, or a
 *   file of this page's release is gone): this page must reload to be coherent;
 * - `applying`: the reload is under way.
 */
export type UpdatePhase = 'current' | 'ready' | 'stale' | 'applying';

/** Why the decision is being taken now. */
export type UpdateTrigger =
  | 'detected' // the new release has just finished downloading
  | 'launch' // the page has just started
  | 'navigation' // the user moved to another page of the app
  | 'resume' // the app came back to the foreground
  | 'user' // the user pressed « Mettre à jour »
  | 'missing-file'; // a file of the running release could not be loaded

export interface UpdateContext {
  phase: UpdatePhase;
  trigger: UpdateTrigger;
  /** A form holds unsaved input (registered drafts or work, or typing since the page opened). */
  unsavedInput: boolean;
  /**
   * The user has touched the page content since it opened (tap, key, stroke).
   * Not proof of unsaved work, but enough to never reload behind their back.
   */
  interacted: boolean;
  /** Offline operations or media are still queued on the device. */
  queuePending: boolean;
  /** A save is being sent to the server right now. */
  saving: boolean;
  online: boolean;
  /** The page is not visible (background tab, app switched away). */
  hidden: boolean;
  /** Milliseconds since the last automatic reload of this tab, if any. */
  sinceLastReload: number | undefined;
  /** Milliseconds since the new release became available to this page. */
  waitingFor: number;
  /**
   * The browser has started leaving this document (address typed, link to
   * another site, reload). It cancels the files still loading: they are not
   * missing, and a reload now would bring the user back to the page they left.
   */
  leaving: boolean;
}

/** `apply`: reload now · `prompt`: let the user decide · `wait`: nothing yet · `fail`: stop, show the error. */
export type UpdateDecision = 'apply' | 'prompt' | 'wait' | 'fail';

export function decideUpdate(context: UpdateContext): UpdateDecision {
  const { phase, trigger } = context;
  const recentlyReloaded =
    context.sinceLastReload !== undefined && context.sinceLastReload < RELOAD_GUARD_MS;

  // The user asked for it: always honoured (drafts are flushed first).
  if (trigger === 'user') return phase === 'applying' ? 'wait' : 'apply';

  // The page cannot work any more. One reload repairs it; a second failure in
  // a row means the reload is not the cure, so the error is shown instead.
  // Offline, a reload could land on the browser's own « no connection » page:
  // the failure is shown inside the application.
  // Not while the browser is leaving the page: that load was cancelled, not lost.
  if (trigger === 'missing-file') {
    if (context.leaving) return 'wait';
    return recentlyReloaded || !context.online ? 'fail' : 'apply';
  }

  if (phase === 'current' || phase === 'applying') return 'wait';

  // Offline, the queue and the cached pages are all the user has: never
  // reload on our own initiative, and do not invite them to.
  if (!context.online) return 'wait';
  // Work that a reload would lose or disturb: the user chooses the moment.
  if (context.unsavedInput || context.queuePending) return 'prompt';
  // A save is on its way: neither reload under it nor bother the user — look again shortly.
  if (context.saving) return 'wait';
  if (recentlyReloaded) return 'prompt';

  // The page content is being replaced anyway: apply.
  if (trigger === 'launch' || trigger === 'navigation') return 'apply';
  // The user has touched this page (a signature drawn, a photo being taken, a
  // choice made): coming back to it must find it as it was. Ask on return,
  // stay silent while it is in the background.
  if (context.interacted) {
    if (context.hidden) return 'wait';
    if (trigger === 'resume') return 'prompt';
  } else if (trigger === 'resume' || context.hidden) {
    // Truly idle: apply on return to the app, or at once in a background tab.
    return 'apply';
  }
  // Visible and idle: stay silent, the next page change will apply it. Only a
  // release that has waited for minutes on the same page is offered.
  return context.waitingFor >= IDLE_PROMPT_AFTER_MS ? 'prompt' : 'wait';
}

/** Whether a check may be sent now (`lastCheckAt` undefined: never checked). */
export function mayCheckNow(now: number, lastCheckAt: number | undefined): boolean {
  return lastCheckAt === undefined || now - lastCheckAt >= UPDATE_CHECK_MIN_GAP_MS;
}

/** Delay before the next periodic check; `random` in [0, 1) spreads devices apart. */
export function nextCheckDelay(random: number): number {
  const spread = (random * 2 - 1) * UPDATE_CHECK_JITTER;
  return Math.round(UPDATE_CHECK_INTERVAL_MS * (1 + spread));
}

/** A dynamic import or stylesheet of the running release that the server no longer has. */
export function isMissingFileError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported|Unable to preload CSS|ChunkLoadError/i
    .test(message);
}

// --- Browser wiring ---------------------------------------------------------------------

export interface UpdateGuards {
  /** Forms with unsaved input that keep a device copy. */
  unsavedDrafts: () => boolean;
  /** Unsaved work that has NO device copy (a signature being drawn): a reload loses it. */
  unsavedWork: () => boolean;
  /** Writes those device copies now. */
  flushDrafts: () => Promise<void>;
  /** A request that changes data is in flight. */
  saving: () => boolean;
  /** Operations or media waiting in the offline queue. */
  queuePending: () => boolean;
}

export interface UpdateSnapshot {
  phase: UpdatePhase;
  /** The prompt « Nouvelle version disponible » is offered. */
  prompt: boolean;
  /** A form with a device copy is being filled: the prompt can promise it is kept. */
  draftsKept: boolean;
  /** Work without a device copy is in progress: the prompt says to save it first. */
  workAtRisk: boolean;
  /** The automatic repair of a missing file was refused (already tried). */
  failed: boolean;
}

/** Every provider registers what it knows; the page is safe only if all agree. */
const guardSources = new Set<Partial<UpdateGuards>>();
const guards: UpdateGuards = {
  unsavedDrafts: () => [...guardSources].some((source) => source.unsavedDrafts?.() ?? false),
  unsavedWork: () => [...guardSources].some((source) => source.unsavedWork?.() ?? false),
  flushDrafts: async () => {
    await Promise.all([...guardSources].map((source) => source.flushDrafts?.()));
  },
  saving: () => [...guardSources].some((source) => source.saving?.() ?? false),
  queuePending: () => [...guardSources].some((source) => source.queuePending?.() ?? false),
};
let snapshot: UpdateSnapshot = { phase: 'current', prompt: false, draftsKept: false, workAtRisk: false, failed: false };
const listeners = new Set<() => void>();
let typedSincePageOpened = false;
let interactedSincePageOpened = false;
let navigations = 0;
let pendingSince: number | undefined;
let lastCheckAt: number | undefined;
let registration: ServiceWorkerRegistration | undefined;
let started = false;
/** The browser announced it is leaving this document, and nothing has shown since that it stayed. */
let leavingPage = false;

function publish(next: Partial<UpdateSnapshot>): void {
  const becamePending = (next.phase === 'ready' || next.phase === 'stale') && pendingSince === undefined;
  if (becamePending) {
    pendingSince = Date.now();
    // Offer it if it is still waiting on the same idle page after a few minutes.
    window.setTimeout(() => evaluate('detected'), IDLE_PROMPT_AFTER_MS + 1000);
  }
  snapshot = { ...snapshot, ...next };
  for (const listener of listeners) listener();
}

export function subscribeToUpdate(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
export function getUpdateSnapshot(): UpdateSnapshot {
  return snapshot;
}

/** True from the moment a reload for a new release has been decided. */
export function updateReloadInProgress(): boolean {
  return snapshot.phase === 'applying';
}

/** Adds a source of « do not reload now » knowledge; the returned function removes it. */
export function registerUpdateGuards(source: Partial<UpdateGuards>): () => void {
  guardSources.add(source);
  return () => {
    guardSources.delete(source);
  };
}

function sinceLastReload(): number | undefined {
  try {
    const at = Number(window.sessionStorage.getItem(RELOAD_AT_KEY));
    return at > 0 ? Date.now() - at : undefined;
  } catch {
    return undefined;
  }
}

function context(trigger: UpdateTrigger): UpdateContext {
  return {
    phase: snapshot.phase,
    trigger,
    unsavedInput: guards.unsavedDrafts() || guards.unsavedWork() || typedSincePageOpened,
    interacted: interactedSincePageOpened,
    queuePending: guards.queuePending(),
    saving: guards.saving(),
    online: navigator.onLine,
    hidden: document.visibilityState === 'hidden',
    sinceLastReload: sinceLastReload(),
    waitingFor: pendingSince === undefined ? 0 : Date.now() - pendingSince,
    leaving: leavingPage,
  };
}

async function reloadOnNewRelease(): Promise<void> {
  publish({ phase: 'applying', prompt: false });
  try {
    await guards.flushDrafts();
  } catch {
    // A copy that cannot be written must not strand the user on a broken page.
  }
  try {
    window.sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  } catch {
    // Without storage the loop guard is simply absent for this tab.
  }
  const waiting = registration?.waiting;
  if (!waiting) {
    window.location.reload();
    return;
  }
  // The reload happens when the new worker has taken control (`controllerchange`);
  // if that message is lost, reload anyway rather than leaving the page hanging.
  window.setTimeout(() => window.location.reload(), 4000);
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

function evaluate(trigger: UpdateTrigger): void {
  const current = context(trigger);
  const decision = decideUpdate(current);
  if (decision === 'apply') void reloadOnNewRelease();
  else if (decision === 'prompt') {
    const workAtRisk = guards.unsavedWork();
    publish({ prompt: true, workAtRisk, draftsKept: guards.unsavedDrafts() && !workAtRisk });
  }
  else if (decision === 'fail') publish({ failed: true });
  else if (snapshot.prompt && !current.online) publish({ prompt: false });
}

/** The user pressed « Mettre à jour ». */
export function applyUpdateNow(): void {
  evaluate('user');
}

/** The router moved to another page: its previous content is gone, a safe moment. */
export function notifyNavigation(): void {
  leavingPage = false;
  typedSincePageOpened = false;
  interactedSincePageOpened = false;
  navigations += 1;
  const opened = navigations;
  // Only a release that was already waiting when the page changed, and that a
  // save in flight kept from being applied, gets a second look. A release that
  // finishes downloading just after a page change waits for the next one, like
  // on any other idle page.
  const heldBySave = (snapshot.phase === 'ready' || snapshot.phase === 'stale') && guards.saving();
  evaluate('navigation');
  if (!heldBySave) return;
  // A save still being sent as the page opens (a common end of form) must not
  // cost the whole opportunity: look again once, if the user has touched nothing.
  window.setTimeout(() => {
    const pending = snapshot.phase === 'ready' || snapshot.phase === 'stale';
    if (pending && opened === navigations && !typedSincePageOpened) evaluate('navigation');
  }, NAVIGATION_RETRY_MS);
}

/**
 * A file of the running release is missing (typically a page chunk requested
 * after a deployment). Returns true when a repairing reload has been started.
 */
export function recoverFromMissingFile(): boolean {
  if (snapshot.phase === 'applying') return true;
  if (leavingPage) {
    // The browser cancels what is still loading when it leaves for another
    // document (WebKit reports it as a failed import). Reloading here would
    // replace the page the user asked for by the one they are leaving. If the
    // departure does not happen after all (a download, a refused « leave this
    // page? »), the page is still here shortly after: repair it then.
    window.setTimeout(() => {
      leavingPage = false;
      recoverFromMissingFile();
    }, DEPARTURE_SETTLE_MS);
    return true;
  }
  if (snapshot.phase === 'current') publish({ phase: 'stale' });
  evaluate('missing-file');
  return getUpdateSnapshot().phase === 'applying';
}

/**
 * A release the server refuses to talk to any longer (version gate): fetch the
 * new one if it is not there yet, then reload on it. A plain reload would show
 * the same old release again while its replacement is still waiting.
 */
export async function forceUpdate(): Promise<void> {
  try {
    await registration?.update();
    const pending = registration?.installing;
    if (pending) {
      await new Promise<void>((resolve) => {
        const done = (): void => {
          if (pending.state === 'installed' || pending.state === 'redundant') resolve();
        };
        pending.addEventListener('statechange', done);
        window.setTimeout(resolve, 8000);
        done();
      });
    }
  } catch {
    // Offline or unsupported: the reload below is the best that can be done.
  }
  await reloadOnNewRelease();
}

async function checkForUpdate(): Promise<void> {
  if (!registration || !navigator.onLine || !mayCheckNow(Date.now(), lastCheckAt)) return;
  lastCheckAt = Date.now();
  try {
    await registration.update();
  } catch {
    // Network error: the next trigger will ask again.
  }
}

function watch(worker: ServiceWorker | null): void {
  if (!worker) return;
  const settle = (): void => {
    // « installed » while another worker is active = a NEW release, waiting.
    const replacesAnother = Boolean(registration?.active) && registration?.active !== worker;
    if (worker.state === 'installed' && replacesAnother && snapshot.phase === 'current') {
      publish({ phase: 'ready' });
      evaluate('detected');
    }
  };
  worker.addEventListener('statechange', settle);
  settle();
}

/**
 * Registers the service worker and starts watching for releases. Call once,
 * in production builds only (the development server has no service worker).
 */
export function startAppUpdates(): void {
  if (started) return;
  started = true;
  // Known before the browser cancels the loads of the page it leaves; a page
  // restored from the back/forward cache has not left.
  window.addEventListener('beforeunload', () => { leavingPage = true; });
  window.addEventListener('pageshow', () => { leavingPage = false; });
  if (!('serviceWorker' in navigator)) return;
  const container = navigator.serviceWorker;
  let controlled = Boolean(container.controller);

  // An installing worker asks whether a page will drive the update. Without an
  // answer it takes over by itself — the only way out for pages released before
  // this mechanism existed, and for a page the phone has frozen.
  container.addEventListener('message', (event: MessageEvent) => {
    if ((event.data as { type?: string } | null)?.type === UPDATE_PING) {
      event.ports[0]?.postMessage(UPDATE_AWARE_REPLY);
    }
  });

  container.addEventListener('controllerchange', () => {
    if (snapshot.phase === 'applying') {
      window.location.reload();
      return;
    }
    // First visit: the worker claims the page, there is no older release here.
    if (!controlled) {
      controlled = true;
      return;
    }
    // Applied from another tab, or taken over while this page was frozen.
    publish({ phase: 'stale' });
    evaluate('detected');
  });

  // Typing anywhere counts as unsaved input until the page changes: many small
  // forms keep no device copy, and a reload on return to the app would erase them.
  document.addEventListener('input', () => {
    const first = !typedSincePageOpened;
    typedSincePageOpened = true;
    // A form started while a release is waiting: offer the update now, since
    // it can no longer be applied silently on this page.
    if (first && (snapshot.phase === 'ready' || snapshot.phase === 'stale')) evaluate('detected');
  }, { capture: true, passive: true });

  // Any tap, stroke or key in the page CONTENT (not the navigation chrome, whose
  // use leads to a page change anyway): signatures, photo capture and choices
  // made without typing leave no `input` event behind.
  const touched = (event: Event): void => {
    const target = event.target instanceof Element ? event.target : null;
    // Using the page proves it is still here, whatever was announced before.
    leavingPage = false;
    if (target?.closest(CHROME_SELECTOR)) return;
    interactedSincePageOpened = true;
  };
  document.addEventListener('pointerdown', touched, { capture: true, passive: true });
  document.addEventListener('keydown', touched, { capture: true, passive: true });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    // Not at once: returning from the camera or a file picker delivers the
    // chosen file just AFTER the page becomes visible again.
    window.setTimeout(() => {
      if (document.visibilityState === 'visible') evaluate('resume');
    }, RESUME_SETTLE_MS);
    void checkForUpdate();
  });
  window.addEventListener('online', () => evaluate('detected'));
  // Vite reports a page chunk or stylesheet it could not load.
  window.addEventListener('vite:preloadError', () => { recoverFromMissingFile(); });

  const schedule = (): void => {
    window.setTimeout(() => {
      if (document.visibilityState === 'visible') void checkForUpdate();
      schedule();
    }, nextCheckDelay(Math.random()));
  };

  const register = async (): Promise<void> => {
    try {
      // `updateViaCache: 'none'`: the check always reaches the server (or its
      // 304), never a copy kept by the HTTP cache.
      registration = await container.register('/sw.js', { scope: '/', updateViaCache: 'none' });
    } catch {
      return; // Private mode or refused: the app works, each launch loads the current release.
    }
    // The browser itself checks on registration: count it, so the first return
    // to the foreground does not ask again.
    lastCheckAt = Date.now();
    registration.addEventListener('updatefound', () => watch(registration?.installing ?? null));
    watch(registration.installing);
    if (registration.waiting && registration.active) {
      // Downloaded during a previous visit and still waiting (another tab was open).
      publish({ phase: 'ready' });
      evaluate('launch');
    }
    schedule();
  };
  if (document.readyState === 'complete') void register();
  else window.addEventListener('load', () => void register(), { once: true });
}

/** Test seam: forget everything (unit tests share the module instance). */
export function resetAppUpdateForTests(): void {
  guardSources.clear();
  snapshot = { phase: 'current', prompt: false, draftsKept: false, workAtRisk: false, failed: false };
  typedSincePageOpened = false;
  interactedSincePageOpened = false;
  navigations = 0;
  pendingSince = undefined;
  lastCheckAt = undefined;
  registration = undefined;
  started = false;
  leavingPage = false;
  listeners.clear();
}
